import React from "react";
import { Phone, ArrowRight, Clock, Wallet, CreditCard, ShoppingBag } from "lucide-react";
import { useSection } from "@/context/SiteContext";
import { resolveImageUrl, telHref } from "@/lib/site";

export default function Hero() {
  const hero = useSection("hero");
  const contact = useSection("contact");
  const tel = telHref(contact.phone_tel, contact.phone);

  const img = resolveImageUrl(hero.image_url);

  const infoItems = [
    { Icon: Clock, label: hero.info_1 || "Öppet till 21:00" },
    { Icon: Wallet, label: hero.info_2 || "100–200 kr" },
    { Icon: CreditCard, label: hero.info_3 || "Kort & kontant" },
    { Icon: ShoppingBag, label: hero.info_4 || "Avhämtning · Servering · Leverans", className: "col-span-2 sm:col-span-1" },
  ];

  return (
    <section className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16 md:pt-16 md:pb-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6 space-y-7">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-[var(--primary)] anim-fade-up">
              <span className="w-6 h-px bg-[var(--primary)]" /> {hero.eyebrow || "Domsjö · Sverige"}
            </span>
            <h1 className="font-[Anton] uppercase text-6xl sm:text-7xl lg:text-[7.5rem] font-normal leading-[0.85] tracking-[0.01em] anim-fade-up anim-delay-1 select-none">
              <span
                className="block text-white"
                style={{ WebkitTextStroke: "2px #1a0e08", textShadow: "0 4px 18px rgba(224,79,57,0.35), 0 0 1px #1a0e08" }}
              >
                {hero.title_line1 || "Restaurang"}
              </span>
              <span
                className="block text-[#F5BE0F]"
                style={{ WebkitTextStroke: "2px #1a0e08", textShadow: "0 4px 18px rgba(224,79,57,0.45), 0 0 1px #1a0e08" }}
              >
                {hero.title_italic || "Mon Ami"}
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-[var(--text-muted)] max-w-xl leading-relaxed anim-fade-up anim-delay-2">
              {hero.subtitle}
            </p>

            <div className="flex flex-wrap gap-3 anim-fade-up anim-delay-3">
              <a href={tel} data-testid="hero-order-button" className="group inline-flex items-center gap-2 rounded-full bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white px-7 py-4 font-semibold shadow-lg shadow-[var(--primary)]/20 transition-all hover:-translate-y-0.5">
                {hero.cta_primary || "Beställ online"}
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </a>
              <a href={tel} data-testid="hero-call-button" className="inline-flex items-center gap-2 rounded-full bg-white text-[var(--text)] border border-[var(--border)] hover:border-[var(--text)] px-6 py-4 font-semibold transition-colors">
                <Phone size={17} /> {hero.cta_secondary || "Ring & boka"}
              </a>
              <a href="#meny" data-testid="hero-menu-button" className="inline-flex items-center gap-2 rounded-full bg-transparent text-[var(--text)] hover:bg-[var(--surface-alt)] px-6 py-4 font-semibold transition-colors underline underline-offset-4 decoration-[var(--primary)]/50 decoration-2">
                {hero.cta_tertiary || "Se meny"}
              </a>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4 anim-fade-up anim-delay-4">
              {infoItems.map(({ Icon, label, className }) => (
                <div key={label} className={`flex items-center justify-center gap-2.5 p-3 rounded-xl bg-white/60 border border-[var(--border)]/70 text-center ${className || ""}`}>
                  <Icon size={18} className="text-[var(--primary)] shrink-0" />
                  <span className="text-sm sm:text-[15px] font-medium text-[var(--text)] leading-tight">{label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 relative anim-fade-up anim-delay-2">
            <div className="relative h-[420px] sm:h-[520px] lg:h-[640px] rounded-3xl overflow-hidden shadow-xl shadow-stone-900/10 bg-[var(--surface-alt)]">
              {img && <img src={img} alt={hero.title_italic || "Mon Ami"} className="w-full h-full object-cover" />}
              <div className="absolute inset-0 bg-gradient-to-tr from-black/30 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-5 left-5 sm:left-8 bg-white rounded-2xl shadow-xl px-5 py-4 flex items-center gap-4 border border-[var(--border)]">
              <div className="text-center pr-4 border-r border-[var(--border)]">
                <div className="font-serif-display text-3xl font-black leading-none">{hero.rating_value || "4.1"}</div>
                <div className="text-[10px] tracking-wide uppercase text-[var(--text-muted)] mt-1">/ 5.0</div>
              </div>
              <div>
                <div className="text-sm font-semibold">{hero.rating_label || "Utmärkta betyg"}</div>
                <div className="text-xs text-[var(--text-muted)]">{hero.rating_count || "300+ gästrecensioner"}</div>
              </div>
            </div>
            {hero.badge_price && (
              <div className="hidden md:flex absolute -top-4 right-6 bg-[var(--text)] text-white rounded-full px-5 py-3 items-center gap-2 shadow-lg rotate-3">
                <span className="font-serif-display italic text-lg">{hero.badge_label || "från"}</span>
                <span className="font-black text-xl">{hero.badge_price}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
